import { useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { CallSplit, Edit, NoAccounts, PersonAddAlt1, Send } from '@mui/icons-material';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { useGlobals } from 'src/shared/contexts/appContext';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { ComposeEmailModal } from 'src/modules/groupMessageEmail/components/composeEmailModal';
import { Member } from '../models/member';
import { MemberResignMemberModal } from './memberResignMemberModal';
import { MemberResignMemberNotAuthorizedModal } from './memberResignMemberNotAuthorizedModal';
import { MemberReenterMemberModal } from './memberReenterMemberModal';
import { MemberEditModal } from './memberEditModal';
import { MemberActiveMemberModal } from './memberActivateMemberModal';
import { MemberActivateMemberNotAuthorizedModal } from './memberActivateMemberNotAuthorizedModal';
import { MemberChangeAssociationModal } from './memberChangeAssociationModal';

type MemberDetailsActionButtonsProps = {
  otherContactMode: boolean;
  member: Member;
  fetchData: (uid: string | number) => void;
};

export const MemberDetailsActionButtons = (props: MemberDetailsActionButtonsProps) => {
  const { t } = useTranslation();
  const { otherContactMode, member, fetchData } = props;
  const { currentUser } = useGlobals();
  const [editMember, setEditMember] = useState<boolean>(false);
  const [resignMember, setResignMember] = useState<boolean>(false);
  const [reenterMember, setReenterMember] = useState<boolean>(false);
  const [activateMember, setActivateMember] = useState<boolean>(false);
  const [changeAssociation, setChangeAssociation] = useState<boolean>(false);
  const [composeEmail, setComposeEmail] = useState<boolean>(false);

  const hasMemberUpdatePermission = getPermission(currentUser, PermissionEnum.MemberUpdate);

  const onActivateEditMemberhandler = () => {
    setEditMember(true);
  };

  const onResignMemberHandler = () => {
    setResignMember(true);
  };
  const onReenterMemberHandler = () => {
    setReenterMember(true);
  };
  const onActivateMemberHandler = () => {
    setActivateMember(true);
  };

  const isNotMember = member?.status?.id === MemberStatusEnum.NotMember;
  const isNewMember = member?.status?.id === MemberStatusEnum.NewMember;
  const hasInvoices = member?.currentInvoices && member?.currentInvoices.length > 0;

  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.label'));
  const associationName = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, t('member:tableHeaders.association'));

  return (
    <>
      {currentUser && (
        <Tooltip arrow title={!member.email ? t('member:memberDetails.memberMissingEmail') : ''} placement='left'>
          <span>
            <Button
              size='small'
              variant='outlined'
              onClick={() => setComposeEmail(true)}
              disableElevation
              endIcon={<Send fontSize='small' />}
              disabled={!member.email}
            >
              {t('member:footer.sendMessage')}
            </Button>
          </span>
        </Tooltip>
      )}

      {currentUser && currentUser.associationCount > 1 && (
        <Button
          size='small'
          variant='outlined'
          onClick={() => setChangeAssociation(true)}
          sx={{ ml: '1rem' }}
          endIcon={<CallSplit fontSize='small' />}
          disabled={member.memberUpdate_id !== null}
        >
          {t('common:button.changeAssociation', { name: associationName })}
        </Button>
      )}

      {isNotMember && (
        <Button
          size='small'
          variant='outlined'
          onClick={onReenterMemberHandler}
          sx={{ ml: '1rem' }}
          endIcon={<PersonAddAlt1 fontSize='small' />}
          disabled={member.memberUpdate_id !== null}
        >
          {otherContactMode ? t('common:button.reenterOtherContact', { label: otherLabel }) : t('common:button.reenterMember')}
        </Button>
      )}
      {!isNotMember && !isNewMember && (
        <Button
          size='small'
          variant='outlined'
          onClick={onResignMemberHandler}
          sx={{ ml: '1rem' }}
          endIcon={<NoAccounts fontSize='small' />}
          disabled={member.memberUpdate_id !== null || !hasMemberUpdatePermission}
        >
          {otherContactMode ? t('common:button.exitOtherContact', { label: otherLabel }) : t('common:button.exitMember')}
        </Button>
      )}
      {isNewMember && (
        <Button
          size='small'
          variant='outlined'
          onClick={onActivateMemberHandler}
          sx={{ ml: '1rem' }}
          endIcon={<PersonAddAlt1 fontSize='small' />}
          disabled={member.memberUpdate_id !== null}
        >
          {otherContactMode ? t('common:button.activateOtherContact', { label: otherLabel }) : t('common:button.activateMemberShip')}
        </Button>
      )}

      <Button
        size='small'
        variant='outlined'
        onClick={onActivateEditMemberhandler}
        sx={{ ml: '1rem' }}
        endIcon={<Edit fontSize='small' />}
        disabled={member.memberUpdate_id !== null || !hasMemberUpdatePermission}
      >
        {t('common:button.edit')}
      </Button>
      {member.member_id && isNewMember && hasInvoices && activateMember && (
        <MemberActivateMemberNotAuthorizedModal modalOpen={activateMember} onClose={() => setActivateMember(false)} />
      )}
      {member.member_id && isNewMember && !hasInvoices && activateMember && (
        <MemberActiveMemberModal
          memberId={member.member_id}
          modalOpen={activateMember}
          onClose={() => setActivateMember(false)}
          onResignSuccess={() => {
            if (member.member_id) {
              fetchData(member.member_id?.toString());
              setResignMember(false);
            }
          }}
        />
      )}
      {member && !member.isMainMember && member.member_id && resignMember && (
        <MemberResignMemberModal
          memberId={member.member_id}
          modalOpen={resignMember}
          onClose={() => setResignMember(false)}
          onResignSuccess={() => {
            if (member.member_id) {
              fetchData(member.member_id?.toString());
              setResignMember(false);
            }
          }}
        />
      )}
      {member && member.isMainMember && resignMember && (
        <MemberResignMemberNotAuthorizedModal modalOpen={resignMember} onClose={() => setResignMember(false)} />
      )}
      {member && member.member_id && isNotMember && reenterMember && (
        <MemberReenterMemberModal
          memberId={member.member_id}
          modalOpen={reenterMember}
          onClose={() => setReenterMember(false)}
          onResignSuccess={(memberId) => {
            if (member.member_id) {
              fetchData(memberId.toString());
              setReenterMember(false);
            }
          }}
        />
      )}
      {member && editMember && (
        <MemberEditModal
          otherContactMode={otherContactMode}
          member={member}
          onClose={() => {
            setEditMember(false);
          }}
          callback={() => {
            if (member.member_id) {
              fetchData(member.member_id.toString());
            }
          }}
          onSave={() => {
            setEditMember(false);
            if (member.member_id) {
              fetchData(member.member_id.toString());
            }
          }}
          modalOpen={editMember}
        />
      )}
      {member && changeAssociation && currentUser && currentUser.associationCount > 1 && (
        <MemberChangeAssociationModal
          member={member}
          modalOpen={changeAssociation}
          onClose={() => setChangeAssociation(false)}
          onSave={() => {
            if (member.member_id) {
              fetchData(member.member_id.toString());
              setChangeAssociation(false);
            }
          }}
        />
      )}

      {composeEmail && <ComposeEmailModal members={[member]} onClose={() => setComposeEmail(false)} modalOpen={composeEmail} />}
    </>
  );
};
